import { AnchorButton, Button } from '@cluey/cluey-components';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import moment from 'moment';
import { generatePath, useHistory } from 'react-router';

import { Student } from '../../../../api/types/student';
import { ReactComponent as ExternalLinkIcon } from '../../../../assets/images/icon-external-link-custom-fill.svg';
import tickIcon from '../../../../assets/images/icon-filled-tick-green-2.svg';
import { PATH_HUB_GENERATE_ASSESSMENT, PATH_HUB_VIEW_ASSESSMENT } from '../../../../util/pagePath';
import { getTokenFromUrl } from '../../home/widgets';
import { StudentAssessmentsEligibilities } from '../../home/widgets/AssessmentsOverviewWidget/types';
import { SUBJECT } from './LearningProfile';

const Status = ({ status }: { status: string }) => {
  let color = '';
  let labelColor = 'text-black';
  let statusLabel = '';
  if (status === 'Completed') {
    color = 'bg-green-5';
    statusLabel = 'Completed';
    labelColor = 'text-white';
  } else if (
    status === 'In Progress' ||
    status === 'Not Accessed' ||
    status === 'Accessed' ||
    status === 'Incomplete'
  ) {
    color = 'bg-yellow-3';
    statusLabel = 'In Progress';
  } else {
    return null;
  }
  return (
    <div
      className={classNames(
        color,
        labelColor,
        'rounded-2xl px-2 py-1 text-xs font-semibold uppercase tracking-1px text-black'
      )}
    >
      {statusLabel}
    </div>
  );
};

const AssessmentCard = ({
  student,
  subject,
  yearLevel,
  studentAssessmentsEligibilities,
}: {
  student: Student;
  subject: string;
  yearLevel: string;
  studentAssessmentsEligibilities: StudentAssessmentsEligibilities;
}) => {
  const mathAssessment = studentAssessmentsEligibilities.mathematicsAssessment;
  const englishAssessment = studentAssessmentsEligibilities.englishAssessment;
  const subjectIsMaths = subject === SUBJECT.MATHS;
  const assessmentStatus = subjectIsMaths ? mathAssessment?.status : englishAssessment?.status;
  const assessment = subjectIsMaths ? mathAssessment : englishAssessment;
  const questionsCorrect = assessment?.questionsCorrect;
  const questionsTotal = assessment?.questionsTotal;
  const score = Math.round((questionsCorrect / questionsTotal) * 100);
  const history = useHistory();

  if (assessmentStatus === 'Completed') {
    const token = getTokenFromUrl(assessment?.studentLink || '');
    const path = generatePath(PATH_HUB_VIEW_ASSESSMENT, { token });
    return (
      <div className="flex flex-col gap-6 rounded-b-lg border border-grey-2 bg-white p-6 shadow-md">
        <div className="flex flex-row justify-between">
          <div>
            <div className="text-lg font-semibold">{`${student?.firstName} scored ${score}% in ${yearLevel} ${assessment?.subject} assessment`}</div>
            <div>Completed on {moment(assessment?.completedAt).format('ddd DD MMM YYYY')}</div>
          </div>

          <div>
            <Status status={assessmentStatus} />
          </div>
        </div>

        <div className="flex flex-row items-center">
          <img alt="questions correct icon" src={tickIcon} className="mr-1" />
          <div className="font-semibold">{`${questionsCorrect} out of ${questionsTotal} questions correct`}</div>
        </div>

        <div>
          <AnchorButton className="group" href={path} target="blank" appearance="reverse">
            <div className="flex flex-row items-center">
              <div className="mt-[2px] font-semibold">See assessment details</div>
              <ExternalLinkIcon className="ml-2 h-4 w-4" fill="#686868" aria-label="external link icon" />
            </div>
          </AnchorButton>
        </div>
      </div>
    );
  } else if (
    assessmentStatus === 'Not Accessed' ||
    assessmentStatus === 'Accessed' ||
    assessmentStatus === 'Incomplete'
  ) {
    const questionsCompleted = assessment?.questionsCompleted || 0;
    return (
      <div className="flex cursor-pointer flex-col gap-6 rounded-b-lg border border-grey-2 bg-white p-6 shadow-md">
        <div className="flex flex-row justify-between">
          <div>
            <div className="text-xl font-semibold">{`${yearLevel} ${capitalize(assessment?.subject)} assessment`}</div>
            <div>{`${questionsCompleted} out of ${questionsTotal} questions completed`}</div>
          </div>

          <div>
            <Status status={assessmentStatus} />
          </div>
        </div>

        <div>
          <AnchorButton className="group" href={assessment?.studentLink} target="blank" appearance="reverse">
            <div className="button-with-icon flex flex-row items-center">
              <div className="mt-[2px]">Continue assessment</div>
              <ExternalLinkIcon className="ml-2 h-4 w-4" fill="#686868" aria-label="external link icon" />
            </div>
          </AnchorButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-b-lg border border-grey-2 bg-white p-6 shadow-md">
      <div>No current assessment</div>
      <div>
        <Button
          type="button"
          className="!font-semibold !normal-case"
          appearance="reverse"
          onClick={() => {
            const path = generatePath(PATH_HUB_GENERATE_ASSESSMENT, { assessmentSubject: subject });
            history.push(path, { data: { student, subject } });
          }}
        >
          <span>{`Generate ${yearLevel} ${subject} assessment link`}</span>
        </Button>
      </div>
    </div>
  );
};

export default AssessmentCard;
